import { Form, Formik } from 'formik'
import React from 'react'

import FieldLabel from '@/components/form/FieldLabel'
import FormGroup from '@/components/form/FormGroup'
import TextInput from '@/components/form/TextInput'
import ActionButton from '@/components/misc/ActionButton'
import PersistentOverlayTrigger from '@/components/misc/PersistentOverlayTrigger'
import { useUpdateCalendarLinkCodeMutation } from '@/components/misc/ReferralLinksTable/hooks/useUpdateCalendarLinkCodeMutation'
import {
  EDIT_CALENDAR_CODE_FORM_FIELDS,
  EditCalendarCodeFormValues,
} from '@/components/misc/ReferralLinksTable/lib/constants'
import { ReferralLinkTableRow } from '@/components/misc/ReferralLinksTable/types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { absoluteUrl } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'

interface Props {
  referralLink: ReferralLinkTableRow
  onHide: () => void
}

const EditReferralLinkModal: React.FC<Props> = ({ referralLink, onHide }) => {
  const updateCalendarLinkCodeMutation = useUpdateCalendarLinkCodeMutation()
  const isProcessing = updateCalendarLinkCodeMutation.isPending
  const previousCodes = referralLink.previousCodes
  const initialValues: EditCalendarCodeFormValues = {
    code: referralLink.currentCompanyCode,
  }

  return (
    <Dialog open onHide={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit calendar link code</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          onSubmit={async (values, formikHelpers) => {
            await updateCalendarLinkCodeMutation.mutateAsync({
              referralLinkId: referralLink.id,
              request: { code: values.code },
              formikHelpers,
            })

            onHide()
          }}
        >
          {({ values, dirty }) => {
            const isPristine = !dirty
            const shouldDisableSubmit = isProcessing || isPristine

            const oldUrl = absoluteUrl(
              ROUTES.referralLink({
                params: {
                  referralLinkId: referralLink.id.toString(),
                  referralLinkCode: referralLink.currentCompanyCode,
                },
              }),
            )

            const newUrl = absoluteUrl(
              ROUTES.referralLink({
                params: {
                  referralLinkId: referralLink.id.toString(),
                  referralLinkCode: '<new-code>',
                },
              }),
            )

            const newUrlWithPlaceholderCode = absoluteUrl(
              ROUTES.referralLink({
                params: {
                  referralLinkId: referralLink.id.toString(),
                  referralLinkCode: '<new-code>',
                },
              }),
            )

            const hasEnteredNewCode = dirty && values.code.trim()

            return (
              <Form>
                <DialogBody>
                  <FormGroup>
                    <FieldLabel className="flex items-center gap-2">
                      <div>Current code</div>

                      {previousCodes.length > 0 && (
                        <span className="text-primary cursor-default text-xs">
                          <PersistentOverlayTrigger
                            id={`previous-code-tooltip-${referralLink.id}`}
                            tooltip={
                              <ul className="mb-0">
                                {previousCodes.map((previousCode) => (
                                  <li key={previousCode.code}>
                                    {previousCode.code}
                                  </li>
                                ))}
                              </ul>
                            }
                          >
                            (Previous codes)
                          </PersistentOverlayTrigger>
                        </span>
                      )}
                    </FieldLabel>

                    <Input
                      type="text"
                      value={referralLink.currentCompanyCode}
                      disabled
                      readOnly
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextInput
                      label="New code"
                      name={EDIT_CALENDAR_CODE_FORM_FIELDS.code}
                      autoFocus
                    />
                  </FormGroup>

                  <Alert variant="info" className="my-4">
                    <AlertDescription>
                      <p className="mb-0">
                        {referralLink.previousCodes[0] ? (
                          <>
                            Please note that links using the old code will still
                            work. For example, if a user visits the link with
                            the old code at{' '}
                            <a href={oldUrl} target="_blank" rel="noreferrer">
                              {oldUrl}
                            </a>
                            , they'll be redirected to the link with the new
                            code at{' '}
                            <span className="font-medium">
                              {hasEnteredNewCode
                                ? newUrl
                                : newUrlWithPlaceholderCode}
                            </span>
                            .
                          </>
                        ) : (
                          <>
                            Please note that links using the old code (
                            <a href={oldUrl} target="_blank" rel="noreferrer">
                              {oldUrl}
                            </a>
                            ) will continue to work.
                          </>
                        )}
                      </p>
                    </AlertDescription>
                  </Alert>
                </DialogBody>

                <DialogFooter>
                  <Button variant="outline-secondary" onClick={onHide}>
                    Cancel
                  </Button>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <ActionButton
                          isProcessing={
                            updateCalendarLinkCodeMutation.isPending
                          }
                          isProcessingText="Updating"
                          type="submit"
                          disabled={shouldDisableSubmit}
                        >
                          Update code
                        </ActionButton>
                      </div>
                    </TooltipTrigger>

                    {isPristine && (
                      <TooltipContent>Code hasn't been updated</TooltipContent>
                    )}
                  </Tooltip>
                </DialogFooter>
              </Form>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditReferralLinkModal
